import React, { useState, useEffect, useRef, useCallback } from 'react';
import { listPayments, voidPayment, PAYMENT_METHOD_LABEL } from '../../utils/receivables';
import { toDateAny } from '../../utils/billingCycle';

// 치과별 받을 돈 원장 (명세 §4-4·§4-6, 계약 §2·§3 — 2026-10-23)
// 보낸 명세서 · 과세 세금계산서 세액(별도 줄) · 입금을 시간순으로 늘어놓고 줄마다 잔액을 보여 준다.
// 입금 기록은 지우지 않고 [취소]만 — 취소된 줄은 줄을 긋고 '취소됨 · 누가 언제'를 남긴다(되살리기 없음).
// 잔액은 저장하지 않는다. 금액 계산 규칙의 정본은 utils/receivables.js(buildLedger) — 여기서는 그 결과(receivable.statements)를 그대로 쓴다.

const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;
const fmtYmd = (ymd) => (ymd ? ymd.replace(/-/g, '.') : '-');
const fmtKstDateTime = (v) => {
    const d = toDateAny(v);
    if (!d) return '';
    return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function ReceivableLedgerPanel({ labId, partnerKey, partnerName, receivable, onAddPayment, onChanged, reloadKey }) {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [voidingId, setVoidingId] = useState(null);   // 취소 사유를 적는 중인 입금 id
    const [voidReason, setVoidReason] = useState('');
    const [voidBusy, setVoidBusy] = useState(false);
    const voidBusyRef = useRef(false);                  // 이중 클릭 가드

    const loadPayments = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            setPayments(await listPayments(labId, partnerKey));
        } catch (e) {
            console.error('[원장] 입금 기록 조회 실패:', e?.code || e?.message);
            setLoadError('입금 기록을 불러오지 못했습니다. 새로고침해 주세요.');
        } finally {
            setLoading(false);
        }
    }, [labId, partnerKey]);

    useEffect(() => { loadPayments(); }, [loadPayments, reloadKey]);

    const confirmVoid = async (p) => {
        if (voidBusyRef.current) return;
        const reason = voidReason.trim();
        if (!reason) { alert('취소 사유를 적어 주세요. (예: 금액 잘못 적음)'); return; }
        if (!window.confirm(`${fmtYmd(p.paidAt)} 입금 ${won(p.amount)} 기록을 취소할까요?\n취소한 기록은 줄이 그어진 채 남고, 되살릴 수 없습니다.`)) return;
        voidBusyRef.current = true;
        setVoidBusy(true);
        try {
            await voidPayment(p.id, reason.slice(0, 40));
            setVoidingId(null);
            setVoidReason('');
            await loadPayments();
            if (onChanged) onChanged();
        } catch (e) {
            console.error('[원장] 입금 취소 실패:', p.id, e?.code || e?.message);
            alert('입금 기록을 취소하지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
            voidBusyRef.current = false;
            setVoidBusy(false);
        }
    };

    // ── 줄 만들기: 명세서(+) · 세액(+, 별도 줄) · 입금(−). 같은 날이면 청구 먼저 ──
    const lines = [];
    (receivable?.statements || []).forEach((s, i) => {
        const no = s.statementNumber || '';   // receivables 항목에 번호가 있으면 표시 (명세서를 따로 읽지 않음)
        lines.push({
            key: `s_${s.id}`, date: s.date || '', order: 0, seq: i, kind: 'statement',
            label: `명세서 보냄${no ? ` · ${no}` : ''}`, amount: s.amount,
            disputed: s.settlementStatus === 'disputed',
        });
        if (s.tax) {
            lines.push({
                key: `t_${s.id}`, date: s.date || '', order: 1, seq: i, kind: 'tax',
                label: `${s.tax < 0 ? '수정 세금계산서 세액(차감)' : '세금계산서 세액'}${no ? ` (${no})` : ''}`, amount: s.tax,
            });
        }
    });
    payments.forEach((p, i) => {
        lines.push({
            key: `p_${p.id}`, date: p.paidAt || '', order: 2, seq: i, kind: 'payment', payment: p,
            label: `입금 · ${PAYMENT_METHOD_LABEL[p.method] || '기타'}`, amount: p.amount, voided: !!p.voidedAt,
        });
    });
    lines.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : (a.order - b.order) || (a.seq - b.seq)));
    let run = 0;
    lines.forEach(l => {
        if (l.kind === 'payment') { if (!l.voided) run -= Number(l.amount) || 0; }
        else run += Number(l.amount) || 0;
        l.balance = run;
    });

    const balance = receivable ? receivable.balance : run;
    const td = { padding: '9px 8px', fontSize: 13, color: '#334155', verticalAlign: 'top', borderBottom: '1px solid #F1F5F9' };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B' }}>{'받을 돈'}</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', fontVariantNumeric: 'tabular-nums' }}>
                        {balance < 0 ? `받을 돈 없음 · ${won(-balance)} 더 받음` : won(balance)}
                    </div>
                </div>
                <button onClick={onAddPayment}
                    style={{ padding: '9px 14px', fontSize: 14, fontWeight: 800, color: '#fff', background: '#4F46E5', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                    {'입금 기록'}
                </button>
            </div>
            {receivable && (
                <div style={{ fontSize: 12.5, color: '#64748B', marginBottom: 6 }}>
                    {`보낸 명세서 ${won(receivable.billed)}${receivable.tax > 0 ? ` + 세액 ${won(receivable.tax)}` : receivable.tax < 0 ? ` − 세액 ${won(-receivable.tax)}` : ''} − 입금 ${won(receivable.paid)}`}
                </div>
            )}
            {receivable?.taxNeedsReview && (
                <div style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 8, background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A', marginBottom: 6 }}>
                    {'과세 계산서 있음 — 세무사에게 확인'}
                </div>
            )}
            {receivable?.draftCount > 0 && (
                <div style={{ fontSize: 12.5, color: '#B45309', marginBottom: 6 }}>
                    {`작성 중 ${won(receivable.draftAmount)} (아직 안 보냄 · ${receivable.draftCount}장) — 받을 돈에 안 들어갑니다`}
                </div>
            )}
            {loadError && (
                <div style={{ margin: '8px 0', padding: '8px 10px', fontSize: 13, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8 }}>{loadError}</div>
            )}

            {loading ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>{'불러오는 중…'}</div>
            ) : lines.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#94A3B8', fontSize: 13, background: '#F8FAFC', borderRadius: 10 }}>{'보낸 명세서·입금 기록이 아직 없습니다.'}</div>
            ) : (
                // 폰: 열 압착 대신 가로 스크롤 (2026-10-24 재점검 P2-15)
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 480, borderCollapse: 'collapse', marginTop: 8 }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                            {['날짜', '내용', '금액', '잔액'].map((h, i) => (
                                <th key={h} style={{ padding: '8px', fontSize: 12, fontWeight: 700, color: '#64748B', textAlign: i >= 2 ? 'right' : 'left', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map(l => {
                            const p = l.payment;
                            const strike = l.voided ? { textDecoration: 'line-through', color: '#94A3B8' } : {};
                            return (
                                <tr key={l.key} style={{ background: l.kind === 'payment' && !l.voided ? '#F7FEF9' : undefined }}>
                                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#64748B', ...strike }}>{fmtYmd(l.date)}</td>
                                    <td style={td}>
                                        <span style={strike}>{l.label}</span>
                                        {l.disputed && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 6, background: '#FFF7ED', color: '#C2410C' }}>{'수정 요청 중'}</span>}
                                        {p?.memo && <span style={{ display: 'block', fontSize: 12, color: '#94A3B8', ...strike }}>{p.memo}</span>}
                                        {l.voided && (
                                            <span style={{ display: 'block', fontSize: 11.5, color: '#64748B', marginTop: 2 }}>
                                                {`취소됨 · ${p.voidedBy && p.voidedBy === labId ? '대표' : (p.voidedBy ? '직원 계정' : '-')} · ${fmtKstDateTime(p.voidedAt) || '방금'}`}
                                                {p.voidReason ? ` · 사유: ${p.voidReason}` : ''}
                                            </span>
                                        )}
                                        {p && !l.voided && voidingId === p.id && (
                                            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                                                <input value={voidReason} maxLength={40} autoFocus
                                                    onChange={e => setVoidReason(e.target.value.slice(0, 40))}
                                                    placeholder="취소 사유 (40자, 환자 이름 적지 마세요)"
                                                    style={{ flex: 1, minWidth: 160, padding: '6px 8px', fontSize: 12.5, border: '1px solid #CBD5E1', borderRadius: 6 }} />
                                                <button onClick={() => confirmVoid(p)} disabled={voidBusy}
                                                    style={{ padding: '6px 10px', fontSize: 12, fontWeight: 700, color: '#fff', background: voidBusy ? '#FCA5A5' : '#DC2626', border: 'none', borderRadius: 6, cursor: voidBusy ? 'wait' : 'pointer' }}>
                                                    {voidBusy ? '취소 중…' : '기록 취소'}
                                                </button>
                                                <button onClick={() => { setVoidingId(null); setVoidReason(''); }} disabled={voidBusy}
                                                    style={{ padding: '6px 10px', fontSize: 12, fontWeight: 700, color: '#475569', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, cursor: 'pointer' }}>
                                                    {'그만두기'}
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: (l.kind === 'payment' || Number(l.amount) < 0) ? '#047857' : '#0F172A', ...strike }}>
                                        {(() => {
                                            // 입금 = 차감, 명세서·세액 = 부호대로 (마이너스 수정계산서 세액은 '− X원')
                                            const signed = l.kind === 'payment' ? -(Number(l.amount) || 0) : (Number(l.amount) || 0);
                                            return signed < 0 ? `− ${won(-signed)}` : `+ ${won(signed)}`;
                                        })()}
                                        {p && !l.voided && voidingId !== p.id && (
                                            <button onClick={() => { setVoidingId(p.id); setVoidReason(''); }}
                                                style={{ display: 'block', marginLeft: 'auto', marginTop: 3, padding: '2px 7px', fontSize: 11, fontWeight: 700, color: '#64748B', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, cursor: 'pointer' }}>
                                                {'취소'}
                                            </button>
                                        )}
                                    </td>
                                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: '#475569' }}>{won(l.balance)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            )}
            <p style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', marginTop: 12 }}>{'입금은 직접 기록한 것만 반영됩니다.'}</p>
            <p style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 2 }}>
                {`${partnerName || '이 치과'} 입금은 먼저 보낸 명세서부터 입금된 것으로 봅니다. 세금계산서 세액은 발행 완료된 과세 계산서만 넣습니다.`}
            </p>
        </div>
    );
}

export default ReceivableLedgerPanel;
